// Runtime configuration for chart-rank enrichment (Milestone M3).
//
// CORS reality (verified against the live feeds, June 2026):
//   GET https://rss.applemarketingtools.com/api/v2/<cc>/apps/top-free/200/apps.json
//   301-redirects to https://rss.marketingtools.apple.com/... and the final
//   200 response carries NO `Access-Control-Allow-Origin` header. Apple's RSS
//   marketing feeds therefore CANNOT be read by a browser fetch() from a
//   GitHub Pages origin, and (unlike the iTunes Search/Lookup API) they do not
//   support a JSONP `callback=` parameter either.
//
// Decision: the static app degrades gracefully — it ships with chart enrichment
// DISABLED, so chart-rank cells render "—" with a short note explaining that
// live ranks require a proxy. A minimal Cloudflare Worker proxy lives in
// /proxy and can be enabled here without changing any app logic. The static
// site never depends on the proxy being deployed.

/**
 * Chart enrichment config.
 *
 * To enable live chart ranks:
 *   1. Deploy proxy/apple-rss-worker.js to Cloudflare Workers (see proxy/README.md).
 *   2. Set CHART_CONFIG.enabled = true and CHART_CONFIG.proxyBase to the worker
 *      origin, e.g. 'https://apple-rss.<your-subdomain>.workers.dev'.
 *
 * proxyBase '' means "fetch Apple directly" — only useful from a non-browser
 * runtime or if Apple ever adds CORS headers. Left here so a developer can flip
 * `enabled` to test the direct path locally (e.g. via a dev server proxy).
 */
export const CHART_CONFIG = {
  // Off by default: the deployable static build must not depend on a proxy.
  enabled: false,
  // Origin of the deployed RSS proxy. Empty = direct (CORS-blocked in browsers).
  proxyBase: '',
  // Feed depth to scan per storefront. PLAN.md targets the top 100–200.
  limit: 100,
  // Max simultaneous feed requests so enrichment is not bursty.
  concurrency: 4,
};

/**
 * Whether chart enrichment should run at all.
 * @returns {boolean}
 */
export function isChartEnrichmentEnabled() {
  return Boolean(CHART_CONFIG.enabled);
}

/**
 * UI localization config (issue #114).
 *
 * The app ships full EN / JA / zh-CN / zh-Hant translation catalogs, but for now we
 * serve the English market by default:
 *   - the language switcher only exposes `publicLocales`,
 *   - locale auto-detection never picks JA/zh-CN/zh-Hant unless those locales
 *     are public, so a Japanese or Chinese browser still loads English by default,
 *   - explicit locale routes such as /zh/ can still render their shipped
 *     catalog when someone enters the URL directly.
 *
 * The translations stay in the build and the runtime keeps working — this is a
 * single switch to re-expose the other languages once the English market is
 * established. Flip `multiLocale` to true to bring them back, no other change.
 */
export const I18N_CONFIG = {
  multiLocale: true,
  publicLocales: ['en'],
};

/**
 * Whether the non-English locales are exposed to users.
 * @returns {boolean}
 */
export function isMultiLocaleEnabled() {
  return Boolean(I18N_CONFIG.multiLocale);
}
